/**
 * routeManifestMap — Maps route paths to their localization manifests.
 *
 * Used by the router `beforeEach` guard to prefetch exactly the translations
 * a page needs before the component mounts, eliminating flash-of-English and
 * unnecessary batch API calls.
 *
 * Paths use the **relative segment** (not the full path) so the mapping
 * works regardless of any parent layout prefix.  The lookup in the router
 * guard normalises `/dashboard` → `dashboard` before consulting this map.
 */
import {
  dashboardManifest,
  revenueManifest,
  leakagesManifest,
  aiInsightsManifest,
  agentManifest,
  integrationsManifest,
  billingManifest,
  teamManifest,
  auditManifest,
  settingsManifest,
  adminLocalizationManifest,
  adminOverridesManifest,
  adminTypographyManifest,
  adminRegionManifest,
  adminLocalizationHealthManifest,
  adminAISuggestionsManifest,
  adminContributionsManifest,
} from "./manifests";

export const routeManifestMap: Record<string, readonly string[]> = {
  // Core pages
  "/dashboard": dashboardManifest,
  "/revenue": revenueManifest,
  "/leakages": leakagesManifest,
  "/ai-insights": aiInsightsManifest,
  "/agent": agentManifest,
  "/integrations": integrationsManifest,
  "/billing": billingManifest,
  "/team": teamManifest,
  "/audit": auditManifest,
  "/settings": settingsManifest,
  // Admin pages
  "/admin/localization": adminLocalizationManifest,
  "/admin/translation-overrides": adminOverridesManifest,
  "/admin/typography": adminTypographyManifest,
  "/admin/region": adminRegionManifest,
  "/admin/localization-health": adminLocalizationHealthManifest,
  "/admin/ai-suggestions": adminAISuggestionsManifest,
  "/admin/contributions": adminContributionsManifest,
};

/**
 * Find the manifest for a given route path.
 * Performs an exact match first, then tries prefix match for dynamic routes
 * (e.g. `/integrations/abc-123` → `/integrations`).
 */
export function getManifestForRoute(
  path: string,
): readonly string[] | undefined {
  // Exact match
  if (routeManifestMap[path]) return routeManifestMap[path];

  // Prefix match — pick the longest matching parent path
  const candidates = Object.keys(routeManifestMap)
    .filter((key) => path.startsWith(key + "/"))
    .sort((a, b) => b.length - a.length);

  return candidates.length ? routeManifestMap[candidates[0]] : undefined;
}
